import { ColumnConfig } from '@components/design/migrated/Table';
import { FeatureOption, FilterKeys, Filters, Option, PageType } from '@utils/pageConfig/Types';
import { formatLabelIntoRevenuePriceTitle, isRevenueType } from '@utils/currencyUtils/CurrencyUtils';
import { SortDirection, SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { SortKeys } from '@models/Products';
import { getSortDirectionLabel, stackedChartSortByOptions } from '@models/PerformanceAnalysisboard';
import { NO_OPTIONS } from '@utils/StringConstants';
import { ViewType } from '@hooks/export/Model';
import { ExploreTabView } from '@components/tab-nav/TabConfig';

export enum SelectorType {
  SHOW_PERFORMANCE_WITHIN,
  SHARE,
  KPI,
  SORTED_BY,
  SEGMENT_BY,
  SALES_TYPE,
  ABSOLUTE,
}

export const BUTTON_MENU_CONFIG = {
  [PageType.EXPLORE]: {
    [ExploreTabView.Leaderboard]: {
      [ViewType.Table]: {
        selectors: [SelectorType.SHOW_PERFORMANCE_WITHIN, SelectorType.SHARE],
      },
      [ViewType.BarChart]: {
        selectors: [SelectorType.SHOW_PERFORMANCE_WITHIN, SelectorType.KPI, SelectorType.SORTED_BY],
      },
    },
    [ExploreTabView.Trends]: {
      [ViewType.LineChart]: {
        selectors: [SelectorType.SHOW_PERFORMANCE_WITHIN, SelectorType.SALES_TYPE, SelectorType.SHARE],
      },
      [ViewType.StackedChart]: {
        selectors: [
          SelectorType.SHOW_PERFORMANCE_WITHIN,
          SelectorType.SALES_TYPE,
          SelectorType.ABSOLUTE,
          SelectorType.SORTED_BY,
        ],
      },
    },
    [ExploreTabView.Segments]: {
      [ViewType.StackedChart]: {
        selectors: [
          SelectorType.SHOW_PERFORMANCE_WITHIN,
          SelectorType.SEGMENT_BY,
          SelectorType.KPI,
          SelectorType.SORTED_BY,
        ],
      },
    },
  },
  // Add more entries for other chart types
};

export const getKpiOptions = (
  kpisConfig: ColumnConfig[],
  volume: string,
  currencyLabel: string,
  getDisplayName: (headerKey: string, volume: string) => string,
): Option[] => {
  return kpisConfig.map((kpi) => ({
    label: isRevenueType(kpi.headerKey)
      ? formatLabelIntoRevenuePriceTitle(getDisplayName(kpi.headerKey, volume), currencyLabel)
      : getDisplayName(kpi.headerKey, volume),
    value: kpi.headerKey,
  }));
};

export const getSortOptions = (
  kpisConfig: ColumnConfig[],
  sorting: SortingOptions,
  isStackedChart: boolean,
): Option[] => {
  if (isStackedChart) {
    return stackedChartSortByOptions.map((sortField) => ({
      label: `${sortField.label} ${getSortDirectionLabel(
        sortField.sortDirection,
        SortKeys[sortField.sortKey as keyof typeof SortKeys],
      )}`,
      value: `${sortField.sortKey}|${sortField.label}|${sortField.sortDirection}`,
    }));
  }
  return (
    kpisConfig
      .find((kpi) => kpi.headerKey === sorting.headerKey)
      ?.sortFields?.map((sortField) => ({
        label: `${sortField.label} ${sortField.sortDirection === SortDirection.asc ? 'Ascending' : 'Descending'}`,
        value: `${sortField.sortKey}|${sortField.sortDirection}`,
      })) || [{ label: 'Absolute Descending', value: '|desc' }]
  );
};

// Remove showPerformanceWithin option from Segment By filter
export const removeDuplicateSegByFilterOption = (selectedOption: Filters[], showPerformanceWithin: FeatureOption) => {
  return selectedOption.filter((x) => x.filterTitle !== showPerformanceWithin.value);
};

export const noFeature: {
  value: string;
  label: string;
  dataType: string;
  feature: string;
  filterKey: FilterKeys;
} = {
  value: '',
  label: NO_OPTIONS,
  dataType: '',
  feature: '',
  filterKey: -1,
};

export const filterOutAddSegFeat = (anotherFeatureOption: Filters[], featureDropdown: FeatureOption): Filters[] => {
  return featureDropdown?.filterKey === FilterKeys.CATEGORY
    ? anotherFeatureOption.filter(
        (featureValue) =>
          featureValue.filterKey !== FilterKeys.ADDFEAT && featureValue.filterKey !== FilterKeys.SEGFEAT,
      )
    : anotherFeatureOption;
};
